import { useState, useEffect } from "react";
import { db } from "../../../firebase";
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, orderBy } from "firebase/firestore";

export const useTasks = (user) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTasks();
  }, [user]);

  const loadTasks = async () => {
    if (!user) {
      setTasks([]);
      return;
    }

    setLoading(true);
    try {
      const tasksRef = collection(db, 'tasks');
      const q = query(
        tasksRef,
        where('userId', '==', user.uid),
        orderBy('createdAt', 'asc')
      );
      
      const snapshot = await getDocs(q);
      const tasksData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
          date: data.date?.toDate ? data.date.toDate() : new Date(data.date)
        };
      });
      
      console.log('Loaded tasks:', tasksData);
      setTasks(tasksData);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (taskData) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      console.log('Creating task:', taskData);
      
      const taskWithUser = {
        ...taskData,
        userId: user.uid,
        createdAt: new Date(),
        completed: false
      };

      const docRef = await addDoc(collection(db, 'tasks'), taskWithUser);
      
      const newTask = {
        id: docRef.id,
        ...taskWithUser
      };

      setTasks(prev => {
        const updated = [...prev, newTask];
        console.log('Updated tasks state:', updated);
        return updated;
      });

      console.log('Task created successfully:', newTask);
      return docRef.id;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, updates);
      
      setTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, ...updates } : task
      ));
      
      console.log('Task updated successfully:', taskId, updates);
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  };

  const toggleTaskCompletion = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      await updateTask(taskId, { completed: !task.completed });
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await deleteDoc(doc(db, 'tasks', taskId));
      setTasks(prev => prev.filter(task => task.id !== taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  };

  return {
    tasks,
    loading,
    createTask,
    updateTask,
    toggleTaskCompletion,
    deleteTask,
    refreshTasks: loadTasks
  };
};